/**
 * Geometry for a `fixedItems` dropdown's options list.
 *
 * Kept out of `mixins/dropdown.js` because it is pure arithmetic over
 * rectangles: testing it through a mounted component would mean asserting
 * against jsdom, which reports every element as 0x0 and so cannot express the
 * overflow this exists to prevent.
 */

// Breathing room between the options list and the edge of the window, so a
// clamped dropdown does not sit flush against it.
export const VIEWPORT_PADDING = 8

/**
 * The `left` a fixed options list should be given.
 *
 * The list is anchored to its trigger's left edge, but only `min-width` ties
 * its width to the trigger -- a long option makes it wider. Anchoring alone
 * therefore pushed a dropdown opened near the right edge of the window (a field
 * in the last grid column, a control in the right sidebar) past that edge,
 * where the overflow was clipped and unreachable. The vertical axis was already
 * clamped this way; this is the missing horizontal half.
 *
 * @param {number} targetLeft Trigger's left edge, viewport coordinates.
 * @param {number} elementWidth Rendered width of the options list.
 * @param {number} viewportWidth Usually `window.innerWidth`.
 * @return {number} A left that keeps the list inside the viewport where it can.
 */
export function clampDropdownLeft(targetLeft, elementWidth, viewportWidth) {
  // Anchored to the trigger unless that would push the list off the right edge.
  // A trigger that already sits at or near the left edge keeps its position:
  // pulling it inward would misalign it from its own trigger to buy padding
  // nobody asked for, and a dropdown flush against a trigger flush against the
  // window is correct.
  const maxLeft = viewportWidth - elementWidth - VIEWPORT_PADDING
  if (targetLeft <= maxLeft) {
    return targetLeft
  }

  // Overflowing: pull it left just far enough to fit, but never past 0 -- a
  // list wider than the viewport would otherwise be pushed off the opposite
  // edge, trading one unreachable overflow for another. `max-width` at the call
  // site is what keeps that case inside the window.
  return Math.max(0, maxLeft)
}
